import { getRegistryBase, getRegistryBaseForWorkspace } from './client'
import type {
  ConfigFieldDefinition,
  ConnectorDefinition,
  RegistryMutationResponse,
  RegistrySinkInstanceStatus,
  RegistrySinkConfig,
  RegistrySourceInstanceStatus,
  RegistrySourceConfig
} from './types'
import type {
  SinkStatus,
  SourceStatus,
  UpdateOptions
} from '@streamnative/pulsar-admin-client-typescript'

// === Workspace-scoped Connector APIs ===

export const listSinksForWorkspace = async (org?: string, workspaceUid?: string) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.get<string[]>(`${basePath}/connectors/sinks`)
}

export const listSourcesForWorkspace = async (org?: string, workspaceUid?: string) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.get<string[]>(`${basePath}/connectors/sources`)
}

export const getSinkInfoForWorkspace = async (
  sinkName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.get<RegistrySinkConfig>(`${basePath}/connectors/sinks/${sinkName}`)
}

export const getSourceInfoForWorkspace = async (
  sourceName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.get<RegistrySourceConfig>(`${basePath}/connectors/sources/${sourceName}`)
}

export const getSinkStatusForWorkspace = async (
  sinkName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.get<SinkStatus>(`${basePath}/connectors/sinks/${sinkName}/status`)
}

export const getSourceStatusForWorkspace = async (
  sourceName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.get<SourceStatus>(`${basePath}/connectors/sources/${sourceName}/status`)
}

export const startSinkForWorkspace = async (
  sinkName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.post<RegistryMutationResponse>(`${basePath}/connectors/sinks/${sinkName}:start`)
}

export const stopSinkForWorkspace = async (
  sinkName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.post<RegistryMutationResponse>(`${basePath}/connectors/sinks/${sinkName}:stop`)
}

export const restartSinkForWorkspace = async (
  sinkName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.post<RegistryMutationResponse>(`${basePath}/connectors/sinks/${sinkName}:restart`)
}

export const deleteSinkForWorkspace = async (
  sinkName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.delete<RegistryMutationResponse>(`${basePath}/connectors/sinks/${sinkName}`)
}

export const startSourceForWorkspace = async (
  sourceName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.post<RegistryMutationResponse>(`${basePath}/connectors/sources/${sourceName}:start`)
}

export const stopSourceForWorkspace = async (
  sourceName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.post<RegistryMutationResponse>(`${basePath}/connectors/sources/${sourceName}:stop`)
}

export const restartSourceForWorkspace = async (
  sourceName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.post<RegistryMutationResponse>(
    `${basePath}/connectors/sources/${sourceName}:restart`
  )
}

export const deleteSourceForWorkspace = async (
  sourceName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.delete<RegistryMutationResponse>(`${basePath}/connectors/sources/${sourceName}`)
}

// === Catalog APIs ===

export const getCatalogSinkList = async () => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<ConnectorDefinition[]>(`${basePath}/catalog/sinks`)
}

export const getCatalogSinkConfig = async (name: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<ConfigFieldDefinition[]>(`${basePath}/catalog/sinks/${name}`)
}

export const getCatalogSourceList = async () => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<ConnectorDefinition[]>(`${basePath}/catalog/sources`)
}

export const getCatalogSourceConfig = async (name: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<ConfigFieldDefinition[]>(`${basePath}/catalog/sources/${name}`)
}

// === Sink APIs (WithDefaults) ===

export const listSinksWithDefaults = async () => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<string[]>(`${basePath}/connectors/sinks`)
}

export const getSinkInfoWithDefaults = async (sinkName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<RegistrySinkConfig>(`${basePath}/connectors/sinks/${sinkName}`)
}

export const registerSinkWithDefaults = async (
  sinkName: string,
  sinkConfig: RegistrySinkConfig,
  serviceAccountName: string,
  data?: any,
  url?: string
) => {
  const { axios, basePath } = getRegistryBase()
  const apiUrl = `${basePath}/connectors/sinks/${sinkName}`
  const formData = new FormData()

  if (data) {
    formData.append('data', data)
  }
  if (url) {
    formData.append('url', url)
  }
  formData.append(
    'sinkConfig',
    new Blob([JSON.stringify(sinkConfig)], { type: 'application/json' })
  )
  formData.append('serviceAccountName', serviceAccountName)
  return axios.post<RegistryMutationResponse>(apiUrl, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

export const updateSinkWithDefaults = async (
  sinkName: string,
  sinkConfig: RegistrySinkConfig,
  serviceAccountName: string,
  updateOptions?: UpdateOptions,
  data?: any,
  url?: string
) => {
  const { axios, basePath } = getRegistryBase()
  const apiUrl = `${basePath}/connectors/sinks/${sinkName}`
  const formData = new FormData()

  if (data) {
    formData.append('data', data)
  }
  if (url) {
    formData.append('url', url)
  }
  formData.append(
    'sinkConfig',
    new Blob([JSON.stringify(sinkConfig)], { type: 'application/json' })
  )
  if (updateOptions) {
    formData.append('updateOptions', JSON.stringify(updateOptions))
  }
  formData.append('serviceAccountName', serviceAccountName)

  return axios.put<RegistryMutationResponse>(apiUrl, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

export const deregisterSinkWithDefaults = async (sinkName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.delete<RegistryMutationResponse>(`${basePath}/connectors/sinks/${sinkName}`)
}

export const getSinkStatusWithDefaults = async (sinkName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<SinkStatus>(`${basePath}/connectors/sinks/${sinkName}/status`)
}

export const getSinkInstanceStatusWithDefaults = async (sinkName: string, instanceId: number) => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<RegistrySinkInstanceStatus>(
    `${basePath}/connectors/sinks/${sinkName}/${instanceId}/status`
  )
}

export const restartSinkWithDefaults = async (sinkName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(`${basePath}/connectors/sinks/${sinkName}:restart`)
}

export const startSinkWithDefaults = async (sinkName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(`${basePath}/connectors/sinks/${sinkName}:start`)
}

export const stopSinkWithDefaults = async (sinkName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(`${basePath}/connectors/sinks/${sinkName}:stop`)
}

export const restartSinkInstanceWithDefaults = async (sinkName: string, instanceId: number) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(
    `${basePath}/connectors/sinks/${sinkName}/${instanceId}:restart`
  )
}

export const startSinkInstanceWithDefaults = async (sinkName: string, instanceId: number) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(
    `${basePath}/connectors/sinks/${sinkName}/${instanceId}:start`
  )
}

export const stopSinkInstanceWithDefaults = async (sinkName: string, instanceId: number) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(
    `${basePath}/connectors/sinks/${sinkName}/${instanceId}:stop`
  )
}

// === Source APIs (WithDefaults) ===

export const listSourcesWithDefaults = async () => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<string[]>(`${basePath}/connectors/sources`)
}

export const getSourceInfoWithDefaults = async (sourceName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<RegistrySourceConfig>(`${basePath}/connectors/sources/${sourceName}`)
}

export const registerSourceWithDefaults = async (
  sourceName: string,
  sourceConfig: RegistrySourceConfig,
  serviceAccountName: string,
  data?: any,
  url?: string
) => {
  const { axios, basePath } = getRegistryBase()
  const apiUrl = `${basePath}/connectors/sources/${sourceName}`
  const formData = new FormData()

  if (data) {
    formData.append('data', data)
  }
  if (url) {
    formData.append('url', url)
  }
  formData.append(
    'sourceConfig',
    new Blob([JSON.stringify(sourceConfig)], { type: 'application/json' })
  )
  formData.append('serviceAccountName', serviceAccountName)
  return axios.post<RegistryMutationResponse>(apiUrl, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

export const updateSourceWithDefaults = async (
  sourceName: string,
  sourceConfig: RegistrySourceConfig,
  serviceAccountName: string,
  updateOptions?: UpdateOptions,
  data?: any,
  url?: string
) => {
  const { axios, basePath } = getRegistryBase()
  const apiUrl = `${basePath}/connectors/sources/${sourceName}`
  const formData = new FormData()

  if (data) {
    formData.append('data', data)
  }
  if (url) {
    formData.append('url', url)
  }
  formData.append(
    'sourceConfig',
    new Blob([JSON.stringify(sourceConfig)], { type: 'application/json' })
  )
  if (updateOptions) {
    formData.append('updateOptions', JSON.stringify(updateOptions))
  }
  formData.append('serviceAccountName', serviceAccountName)
  return axios.put<RegistryMutationResponse>(apiUrl, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

export const deregisterSourceWithDefaults = async (sourceName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.delete<RegistryMutationResponse>(`${basePath}/connectors/sources/${sourceName}`)
}

export const getSourceStatusWithDefaults = async (sourceName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<SourceStatus>(`${basePath}/connectors/sources/${sourceName}/status`)
}

export const getSourceInstanceStatusWithDefaults = async (
  sourceName: string,
  instanceId: number
) => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<RegistrySourceInstanceStatus>(
    `${basePath}/connectors/sources/${sourceName}/${instanceId}/status`
  )
}

export const restartSourceWithDefaults = async (sourceName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(
    `${basePath}/connectors/sources/${sourceName}:restart`
  )
}

export const startSourceWithDefaults = async (sourceName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(`${basePath}/connectors/sources/${sourceName}:start`)
}

export const stopSourceWithDefaults = async (sourceName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(`${basePath}/connectors/sources/${sourceName}:stop`)
}

export const restartSourceInstanceWithDefaults = async (sourceName: string, instanceId: number) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(
    `${basePath}/connectors/sources/${sourceName}/${instanceId}:restart`
  )
}

export const startSourceInstanceWithDefaults = async (sourceName: string, instanceId: number) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(
    `${basePath}/connectors/sources/${sourceName}/${instanceId}:start`
  )
}

export const stopSourceInstanceWithDefaults = async (sourceName: string, instanceId: number) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(
    `${basePath}/connectors/sources/${sourceName}/${instanceId}:stop`
  )
}
